import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { rupees } from "@/lib/format";

/**
 * The public pricing page (`BILLING_SPEC.md` §8's own "Required public pages" list; §5's deferred
 * build-order item). Reads `GET /pricing` — a city-scoped but sign-in-free read (the same
 * `x-city-token` every other visitor page already carries via the axios interceptor, `lib/api.js`)
 * — never a second, hand-typed copy of a price or a threshold: every number on this page is the
 * SAME rate-card/config value the backend actually enforces.
 */
export default function Pricing() {
  const { t, tenant } = useApp();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setData(null);
    setFailed(false);
    api
      .get("/pricing")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, [tenant]);

  if (failed) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-center" data-testid="pricing-error">
        <p className="text-muted-foreground">{t("common.connectionTrouble")}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-center text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  const tiers = ["free", "growth", "premium"];

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:py-16" data-testid="pricing-page">
      <h1 className="text-center font-heading text-4xl font-bold tracking-tight sm:text-5xl">
        {t("pricing.title")}
      </h1>
      <p className="mx-auto mt-3 max-w-xl text-center text-base text-muted-foreground">
        {t("pricing.subtitle", { days: data.trial_days })}
      </p>

      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {tiers.map((tier) => {
          const plan = data.plans[tier];
          const isGrowth = tier === "growth";
          return (
            <div
              key={tier}
              className={`relative rounded-2xl border p-7 ${
                isGrowth ? "border-2 border-primary shadow-lg" : "border-border"
              }`}
              data-testid={`pricing-card-${tier}`}
            >
              {isGrowth && (
                <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                  {t("pricing.mostChoose")}
                </span>
              )}
              <h2 className="font-heading text-xl font-bold">{t(`owner.plan.tier.${tier}`)}</h2>
              <p className="mt-3 font-heading text-3xl font-extrabold">
                {plan.monthly_minor_units === 0
                  ? t("owner.plan.free")
                  : rupees({ minor_units: plan.monthly_minor_units })}
                {plan.monthly_minor_units > 0 && (
                  <span className="text-base font-medium text-muted-foreground"> {t("owner.plan.perMonth")}</span>
                )}
              </p>
              {plan.monthly_minor_units > 0 && (
                <p className="mt-1 text-xs text-muted-foreground" data-testid={`pricing-gst-${tier}`}>
                  {t("pricing.gstInclusive", {
                    base: rupees({ minor_units: plan.base_minor_units }),
                    gst: rupees({ minor_units: plan.gst_minor_units }),
                  })}
                </p>
              )}
              {plan.monthly_minor_units > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("pricing.orAnnual", { amount: rupees({ minor_units: plan.annual_minor_units }) })}
                </p>
              )}
              <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                {(tier === "free"
                  ? ["listing", "catalog"]
                  : tier === "growth"
                  ? ["everythingFree", "campaigns", "memberships"]
                  : ["everythingGrowth", "insights", "inventory"]
                ).map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="mt-[2px] h-4 w-4 shrink-0 text-primary" />
                    {t(`owner.plan.planFeature.${f}`)}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground" data-testid="pricing-thresholds">
        {t("pricing.thresholds", {
          members: data.premium_thresholds.members,
          orders: data.premium_thresholds.orders_per_month,
        })}
      </p>
    </div>
  );
}
